class CartDrawer extends HTMLElement {
  constructor() {
    super()

    this.isOpen = false
    this.activeElement = null
    this.splideInstance = null

    this.addEventListener("keyup", (evt) => evt.code === "Escape" && this.close())
    this.bindEvents()
    this.setHeaderCartIconAccessibility()
    console.log("Cart Drawer Initialized...")
    window.initCartRecommendationsSlider();
  }

  bindEvents() {
    // Bind overlay click event
    const overlay = this.querySelector("#CartDrawer-Overlay")
    if (overlay) {
      overlay.addEventListener("click", this.close.bind(this))
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLinks = document.querySelectorAll(".cart-open-trigger")
    cartLinks.forEach((link) => {
      link.setAttribute("role", "button")
      link.setAttribute("aria-haspopup", "dialog")
      link.setAttribute("aria-expanded", "false")

      // Remove existing listeners to prevent duplicates
      const clickHandler = this.handleCartLinkClick.bind(this)
      const keydownHandler = this.handleCartLinkKeydown.bind(this)

      link.removeEventListener("click", clickHandler)
      link.removeEventListener("keydown", keydownHandler)

      // Add new listeners
      link.addEventListener("click", clickHandler)
      link.addEventListener("keydown", keydownHandler)
    })
  }

  handleCartLinkClick(event) {
    event.preventDefault()
    this.open(event.currentTarget)
  }

  handleCartLinkKeydown(event) {
    if (event.code.toUpperCase() === "SPACE" || event.code === "Enter") {
      event.preventDefault()
      this.open(event.currentTarget)
    }
  }

  open(triggeredBy) {
    if (this.isOpen) return

    if (triggeredBy) {
      this.setActiveElement(triggeredBy)
      triggeredBy.setAttribute("aria-expanded", "true")
    }

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary')
    if (cartDrawerNote && !cartDrawerNote.hasAttribute("role")) {
      this.setSummaryAccessibility(cartDrawerNote)
    }

    // Add classes for animation
    this.classList.add("animate")

    // Use requestAnimationFrame for better animation timing
    requestAnimationFrame(() => {
      this.classList.add("active")
      this.isOpen = true
    })

    // Handle focus trapping after animation completes
    this.addEventListener("transitionend", this.handleTransitionEnd.bind(this), { once: true })

    // Prevent body scroll
    document.body.classList.add("overflow-hidden")

    // Update cart icon accessibility
    this.updateCartIconAccessibility(true)
  }

  handleTransitionEnd() {
    const containerToTrapFocusOn = this.classList.contains("is-empty")
      ? this.querySelector(".drawer__inner-empty")
      : document.getElementById("CartDrawer")

    const focusElement = this.querySelector(".drawer__inner") || this.querySelector(".drawer__close")

    if (typeof trapFocus === "function" && containerToTrapFocusOn) {
      trapFocus(containerToTrapFocusOn, focusElement)
    }
  }

  close() {
    if (!this.isOpen) return

    this.classList.remove("active")
    this.isOpen = false

    // Remove focus trap
    if (typeof removeTrapFocus === "function" && this.activeElement) {
      removeTrapFocus(this.activeElement)
    }

    // Restore body scroll
    document.body.classList.remove("overflow-hidden")

    // Update cart icon accessibility
    this.updateCartIconAccessibility(false)

    // Remove animate class after transition
    setTimeout(() => {
      this.classList.remove("animate")
    }, 300)
  }

  updateCartIconAccessibility(isOpen) {
    const cartLinks = document.querySelectorAll(".cart-open-trigger")
    cartLinks.forEach((link) => {
      link.setAttribute("aria-expanded", isOpen.toString())
    })
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute("role", "button")
    cartDrawerNote.setAttribute("aria-expanded", "false")

    if (cartDrawerNote.nextElementSibling && cartDrawerNote.nextElementSibling.getAttribute("id")) {
      cartDrawerNote.setAttribute("aria-controls", cartDrawerNote.nextElementSibling.id)
    }

    cartDrawerNote.addEventListener("click", (event) => {
      const isExpanded = !event.currentTarget.closest("details").hasAttribute("open")
      event.currentTarget.setAttribute("aria-expanded", isExpanded.toString())
    })

    const onKeyUpEscape = (event) => {
      if (event.code === "Escape") {
        this.close()
      }
    }

    if (typeof onKeyUpEscape === "function") {
      cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape)
    }
  }

  // Updated to use the external cleanup function
  cleanupQuickAddListeners() {
    if (typeof window.cleanupAllQuickAdd === "function") {
      window.cleanupAllQuickAdd()
    } else {
      // Fallback cleanup if external function not available
      document.querySelectorAll('[data-quick-add-initialized="true"]').forEach((wrapper) => {
        if (wrapper.outsideClickHandler) {
          document.removeEventListener("click", wrapper.outsideClickHandler)
        }

        const trigger = wrapper.querySelector(".custom-product__cart-trigger")
        if (trigger && trigger.quickAddHandler) {
          trigger.removeEventListener("click", trigger.quickAddHandler)
        }

        const sizeOptions = wrapper.querySelectorAll(".custom-product__size-option")
        sizeOptions.forEach((option) => {
          if (option.quickAddHandler) {
            option.removeEventListener("click", option.quickAddHandler)
          }
        })

        const addToCartButton = wrapper.querySelector("[data-add-to-cart]")
        if (addToCartButton && addToCartButton.quickAddHandler) {
          addToCartButton.removeEventListener("click", addToCartButton.quickAddHandler)
        }

        wrapper.dataset.quickAddInitialized = "false"
      })
    }
  }

  renderContents(parsedState) {
    console.log("Rendering cart contents:", parsedState)

    const drawerInner = this.querySelector(".drawer__inner")

    if (drawerInner && drawerInner.classList.contains("is-empty")) {
      drawerInner.classList.remove("is-empty")
    }

    if (parsedState.id) {
      this.productId = parsedState.id
    }

    // Update all sections with improved error handling
    this.getSectionsToRender().forEach((section) => {
      try {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id)

        if (sectionElement && parsedState.sections && parsedState.sections[section.id]) {
          console.log(`Updating section: ${section.id}`)

          const newHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section)
          if (newHTML) {
            sectionElement.innerHTML = newHTML
            console.log(`Successfully updated section: ${section.id}`)
          }
        } else {
          if (!sectionElement) {
            console.warn(
              `Section element not found for: ${section.id} (selector: ${section.selector || "#" + section.id})`,
            )
          }
          if (!parsedState.sections || !parsedState.sections[section.id]) {
            console.warn(`No content found for section: ${section.id}`)
          }
        }
      } catch (error) {
        console.error(`Error updating section ${section.id}:`, error)
      }
    })

    setTimeout(() => {
      this.bindEvents()
      this.setHeaderCartIconAccessibility()

      if (typeof window.initCartRecommendationsComplete === "function") {
        console.log("Using complete cart recommendations initialization...")
        window.initCartRecommendationsComplete()
      }

      if (!this.isOpen) {
        this.open()
      }
    }, 300) // Reduced timeout since external functions handle their own timing
  }

  getSectionInnerHTML(html, section) {
    try {
      const parser = new DOMParser()
      const doc = parser.parseFromString(html, "text/html")

      // Try multiple selectors for flexibility
      const possibleSelectors = [
        section.selector,
        ".shopify-section",
        `#${section.id}`,
        "[data-section-type]",
        "section",
        "div",
      ].filter(Boolean)

      for (const selector of possibleSelectors) {
        const element = doc.querySelector(selector)
        if (element) {
          console.log(`Found content using selector: ${selector} for section: ${section.id}`)
          return element.innerHTML
        }
      }

      // If no selector works, return the body content or full HTML
      const bodyContent = doc.body
      if (bodyContent && bodyContent.innerHTML.trim()) {
        console.log(`Using body content for section: ${section.id}`)
        return bodyContent.innerHTML
      }

      console.warn(`No suitable content found for section: ${section.id}`)
      return html // Return original HTML as fallback
    } catch (error) {
      console.error("Error parsing section HTML:", error)
      return ""
    }
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
        selector: "#cart-icon-bubble",
      },
      {
        id: "cart-icon-bubble-mobile",
        selector: "#cart-icon-bubble-mobile",
      },
    ]
  }

  getSectionDOM(html, selector = ".shopify-section") {
    try {
      return new DOMParser().parseFromString(html, "text/html").querySelector(selector)
    } catch (error) {
      console.error("Error parsing section DOM:", error)
      return null
    }
  }

  setActiveElement(element) {
    this.activeElement = element
  }

}

customElements.define("cart-drawer", CartDrawer)

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
      {
        id: "cart-icon-bubble-mobile",
        section: "cart-icon-bubble-mobile",
        selector: ".shopify-section",
      },
      {
        id: "#cart-recommendations",
        section: "cart-drawer",
        selector: ".cart-recommendations-slider",
      }
    ]
  }
}

customElements.define("cart-drawer-items", CartDrawerItems)

// Enhanced Quick Add Integration with better cart icon detection
document.addEventListener("DOMContentLoaded", () => {
  // Find and update cart icons more flexibly
  function updateCartIcons() {
    // Common cart icon selectors
    const cartIconSelectors = [
      ".cart-count-bubble",
      ".cart-icon-bubble",
      ".cart-count",
      "[data-cart-count]",
      ".header__icon--cart .cart-count",
      ".cart-link .cart-count",
      ".site-header__cart-count",
    ]

    fetch("/cart.js")
      .then((response) => response.json())
      .then((cart) => {
        const itemCount = cart.item_count

        cartIconSelectors.forEach((selector) => {
          const elements = document.querySelectorAll(selector)
          elements.forEach((element) => {
            if (element) {
              element.textContent = itemCount
              element.style.display = itemCount > 0 ? "block" : "none"
            }
          })
        })
      })
      .catch((error) => {
        console.error("Error updating cart icons:", error)
      })
  }

  // Override fetch to handle cart updates better
  const originalFetch = window.fetch
  window.fetch = function (...args) {
    const [url, options] = args

    // Check if this is a cart add request
    if (url.includes("/cart/add.js") && options && options.method === "POST") {
      return originalFetch.apply(this, args).then((response) => {
        if (response.ok) {
          // Update cart icons after successful add
          setTimeout(() => {
            updateCartIcons()
          }, 100)
        }
        return response
      })
    }

    return originalFetch.apply(this, args)
  }
})
